import {
  Box, Button, Divider, SelectChangeEvent, Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { BarChart } from '@mui/x-charts';
import { useQuery } from '@apollo/client';
import EmptyPageMessage from './EmptyPageMessage';
import { colors } from '../../theme';
import { colorsInterpolationFn } from '../../utilities';
import BehaviorConcernsLoader from './BehaviorConcernsLoader';
import DateFilter from '../DateFilter';
import { GET_BEHAVIOR_CONCERNS_TAB } from '../../queries';
import FeedbackFiltersDialog from '../FeedbackFiltersDialog';

function BehaviorConcernsGraphsTab({
  awoName,
  dogAdopterTypeformId,
  dogFosterTypeformId,
  catAdopterTypeformId,
  catFosterTypeformId,
  showDogs,
  showCats,
}: {
  awoName: string,
  dogAdopterTypeformId: number,
  dogFosterTypeformId: number,
  catAdopterTypeformId: number,
  catFosterTypeformId: number,
  showDogs: boolean,
  showCats: boolean,
}) {
  const [daysAgo, setDaysAgo] = React.useState(-1);
  const [filterDialogOpen, setFilterDialogOpen] = React.useState(false);
  const [appliedFilters, setAppliedFilters] = React.useState<Record<string, boolean>>({
    adopted: true,
    fostered: true,
  });

  // Map appliedFilters to determine which typeform IDs to use
  const mappedFilters = React.useMemo(() => {
    const dogTypeformIds = [];
    const catTypeformIds = [];

    if (appliedFilters.adopted) dogTypeformIds.push(dogAdopterTypeformId);
    if (appliedFilters.fostered) dogTypeformIds.push(dogFosterTypeformId);

    if (appliedFilters.adopted) catTypeformIds.push(catAdopterTypeformId);
    if (appliedFilters.fostered) catTypeformIds.push(catFosterTypeformId);

    return { dogTypeformIds, catTypeformIds };
  }, [appliedFilters, dogAdopterTypeformId, dogFosterTypeformId, catAdopterTypeformId, catFosterTypeformId]);

  const { data, loading, refetch } = useQuery(GET_BEHAVIOR_CONCERNS_TAB, {
    variables: {
      feedbackDaysAgo: daysAgo,
      dogTypeformIds: [dogAdopterTypeformId, dogFosterTypeformId],
      catTypeformIds: [catAdopterTypeformId, catFosterTypeformId],
    },
    skip: !awoName,
  });

  // Refetch when daysAgo or appliedFilters changes
  React.useEffect(() => {
    if (awoName) {
      refetch({
        feedbackDaysAgo: daysAgo,
        dogTypeformIds: mappedFilters.dogTypeformIds,
        catTypeformIds: mappedFilters.catTypeformIds,
      });
    }
  }, [daysAgo, appliedFilters, awoName, refetch, mappedFilters]);

  // Transform the data to match behaviourData format
  const behaviourData = useMemo(() => {
    if (!data) return { dogIssues: [], catIssues: [] };

    const dogData = data.awoDogBehavioralIssues;
    const dogBaseline = data.baselineDogBehavioralIssues;
    const catData = data.awoCatBehavioralIssues;
    const catBaseline = data.baselineCatBehavioralIssues;

    // Helper function to calculate percentage
    const calculatePercentage = (count: number, total: number) => (total > 0
      ? Math.round((count / total) * 100)
      : 0
    );

    // Transform dog data
    const dogIssues = Object.entries(dogData.issues).map(([issue, count]) => ({
      label: issue,
      [awoName]: calculatePercentage(count as number, dogData.numResponses),
      Baseline: calculatePercentage(
        dogBaseline.issues[issue] || 0,
        dogBaseline.numResponses,
      ),
    }));

    // Transform cat data
    const catIssues = Object.entries(catData.issues).map(([issue, count]) => ({
      label: issue,
      [awoName]: calculatePercentage(count as number, catData.numResponses),
      Baseline: calculatePercentage(
        catBaseline.issues[issue] || 0,
        catBaseline.numResponses,
      ),
    }));

    return { dogIssues, catIssues };
  }, [data, awoName]);

  const behaviorDogColors = useMemo(() => behaviourData.dogIssues
    .map((_, idx) => colorsInterpolationFn({
      length: behaviourData?.dogIssues?.length,
      minColor: '#F2E7FE',
      maxColor: '#23036A',
    })(idx)), [behaviourData.dogIssues]);

  const behaviorCatColors = useMemo(() => behaviourData.catIssues
    .map((_, idx) => colorsInterpolationFn({
      length: behaviourData?.catIssues?.length,
      minColor: '#C8FFF4',
      maxColor: '#005457',
    })(idx)), [behaviourData.catIssues]);

  const catIssueMap = {
    Aggression: 'Aggression',
    Destructive: 'Destructive Chewing & Scratching',
    Vocalization: 'Excessive Vocalization',
    Fear: 'Fear & Hiding',
    'Cat Conflict': 'Conflict w/ Resident Cat(s)',
    'Dog Conflict': 'Conflict w/ Resident Dog(s)',
    Jumping: 'Jumping on Unwanted Surfaces',
    'Litter Box': 'Litter Box Issues',
    Other: 'Other Behavior Concerns',
  };

  const dogIssueMap = {
    Reactivity: 'Reactivity & Aggression',
    Chewing: 'Destructive Chewing',
    Barking: 'Barking',
    Fear: 'Fear & Timidity',
    Guarding: 'Resource Guarding',
    Anxiety: 'Anxiety',
    'House Training': 'House Training',
    'Cat Conflict': 'Conflict w/ Resident Cat(s)',
    'Dog Conflict': 'Conflict w/ Resident Dog(s)',
    Jumping: 'Jumping',
    Mouthiness: 'Mouthiness & Nipping',
    'Leash Pulling': 'Leash Pulling',
    Other: 'Other Behavior Concerns',
  };

  return (
    <>
      <Box
        sx={{
          px: 3,
        }}
      >
        {(!data || loading) && <BehaviorConcernsLoader />}
        {!(!data || loading)
          && data.awoDogBehavioralIssues.numResponsesNoFilters === 0
          && data.awoCatBehavioralIssues.numResponsesNoFilters === 0
          && <EmptyPageMessage />}
        {
          !(!data || loading)
            && (data.awoDogBehavioralIssues.numResponsesNoFilters > 0
              || data.awoCatBehavioralIssues.numResponsesNoFilters > 0)
            && (
            <>
              <Box sx={{
                display: 'flex',
                gap: 1,
                background: colors.grey['50'],
                px: 2.38,
                py: 0.5,
              }}
              >
                <DateFilter
                  formControl={{
                    variant: 'standard',
                    sx: { bgcolor: 'transparent' },
                  }}
                  selectedDate={daysAgo}
                  onChange={(e: SelectChangeEvent<number>) => {
                    setDaysAgo(Number(e.target.value));
                  }}
                />
                <Button
                  variant="text"
                  size="small"
                  color="black"
                  onClick={() => setFilterDialogOpen(true)}
                >
                  Filters
                </Button>
              </Box>
              {showDogs && (
                <>
                  <Box
                    sx={{
                      py: 2,
                    }}
                  >
                    <Typography variant="h5">Behavior concerns for dogs compared 🐶</Typography>
                    <Typography variant="body2">
                      Here are the reported behavioral issues for dogs adopted or
                      fostered from your organization, compared to the baseline
                      of other animal welfare organizations using Petcademy
                    </Typography>
                  </Box>
                  <Box>
                    <BarChart
                      sx={{
                        '.MuiBarElement-series-baseLineBar': {
                          fill: colors.grey['100'],
                        },
                      }}
                      xAxis={[{
                        scaleType: 'band',
                        dataKey: 'label',
                        colorMap: {
                          type: 'ordinal',
                          colors: behaviorDogColors,
                        },
                        valueFormatter: (item, context) => (context.location === 'tooltip'
                            ? dogIssueMap[item] || item
                            : item),
                      }]}
                      series={[
                        {
                          dataKey: awoName,
                          label: awoName,
                          id: 'awoBar',
                          valueFormatter: (item) => `${item} %`,
                        },
                        {
                          dataKey: 'Baseline',
                          label: 'Baseline',
                          color: colors.grey['100'],
                          id: 'baseLineBar',
                          valueFormatter: (item) => `${item} %`,
                        },
                      ]}
                      height={300}
                      dataset={behaviourData.dogIssues}
                      slotProps={{ legend: { hidden: true } }}
                      tooltip={{
                        classes: {
                          root: 'behaviorGraphTooltip',
                        },
                      }}
                    />
                  </Box>
                </>
              )}
              {showCats && showDogs && <Divider sx={{ my: 3 }} />}
              {showCats && (
                <>
                  <Box
                    sx={{
                      py: 2,
                    }}
                  >
                    <Typography variant="h5">Behavior concerns for cats compared 🐱</Typography>
                    <Typography variant="body2">
                      Here are the reported behavioral issues for cats adopted or
                      fostered from your organization, compared to the baseline
                      of other animal welfare organizations using Petcademy
                    </Typography>
                  </Box>
                  <Box>
                    <BarChart
                      sx={{
                        '.MuiBarElement-series-baseLineBar': {
                          fill: colors.grey['100'],
                        },
                      }}
                      xAxis={[{
                        scaleType: 'band',
                        dataKey: 'label',
                        colorMap: {
                          type: 'ordinal',
                          colors: behaviorCatColors,
                        },
                        valueFormatter: (item, context) => (context.location === 'tooltip'
                            ? catIssueMap[item] || item
                            : item),
                      }]}
                      series={[
                        {
                          dataKey: awoName,
                          label: awoName,
                          id: 'awoBar',
                          valueFormatter: (item) => `${item} %`,
                        },
                        {
                          dataKey: 'Baseline',
                          label: 'Baseline',
                          color: colors.grey['100'],
                          id: 'baseLineBar',
                          valueFormatter: (item) => `${item} %`,
                        },
                      ]}
                      height={300}
                      dataset={behaviourData.catIssues}
                      slotProps={{ legend: { hidden: true } }}
                      tooltip={{
                        classes: {
                          root: 'behaviorGraphTooltip',
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </>
          )
        }
      </Box>
      <FeedbackFiltersDialog
        open={filterDialogOpen}
        setOpen={setFilterDialogOpen}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        showCareType
        showAnimalType={false}
      />
    </>
  );
}

export default BehaviorConcernsGraphsTab;
